import styled, { keyframes } from 'styled-components';
import { Form } from 'formik';
import { Chip, Typography } from '@mui/material';

import colors from '../../../../../styles/colors';
import {
  SectionCompartment,
} from '../../../../../components/v2/Section';
import FormikInput from '../../../../../components/v2/FormikInput';
import RadioButtonGroup from '../../../../components/RadioButtonGroup';

import { COMPONENT_REARRANGE_SCREEN_WIDTH } from './utils';

const FLASH_SECONDS = 2;
const FLASH_CLASSNAME = 'flash';

// Keyframes for the flash animation
const flashShadow = keyframes`
  0% {
    box-shadow: inset 0 0 10px 2px rgba(0, 0, 0, 0.3);
  }
  100% {
    box-shadow: inset 0 0 0 0 rgba(0, 0, 0, 0);
  }
`;

const Container = styled.div`
  width: 100%;
  height: 100%;

  @media (max-width: 1240px) {
    height: fit-content;
  }
`;

const StyledForm = styled(Form)`
  display: flex;
  width: 50%;
  min-width: 700px;
  flex: 0 0 auto;
  flex-direction: column;
  border: 1px solid ${colors.shades.secondary7};
  border-radius: 5px;

  @media (max-width: 1240px) {
    width: 100%;
  }
`;

const StyledSectionCompartment = styled(SectionCompartment)`
  width: unset;
  row-gap: 5px;
  padding: 10px;
  flex-grow: 1;
`;

const ContentContainer = styled.div`
  display: flex;
  justify-content: stretch;
  height: 100%;
  position: relative;
  max-height: calc(100% - 76px);
  overflow-y: auto;
  align-items: flex-start;
  padding-bottom: 20px;
  gap: 20px;
  margin-top: 0;

  @media (max-width: 1240px) {
    flex-direction: column;
    height: fit-content;
    align-items: center;
  }
`;

const InfoChip = styled(Chip)`
  background-color: ${({ $isRest }) => ($isRest ? colors.shades.secondary9 : colors.shades.primary3)};
  height: 25px;
  margin-right: 15px;
  width: fit-content;
`;

const ScrollableContainer = styled.div`
  flex: 1;
  width: 100%;
  min-width: 220px;
  max-height: 100%;
  position: sticky;
  top: 0;
  right: 0;
  overflow-y: auto;
  scrollbar-width: none;

  .MuiDataGrid-root {
    min-width: 500px;
  }

  @media (max-width: ${COMPONENT_REARRANGE_SCREEN_WIDTH}px) {
    position: static;
    width: 100%;
    max-height: 500px;
  }

  &.${FLASH_CLASSNAME} {
    animation: ${flashShadow} ${FLASH_SECONDS}s ease-in-out;
  }
`;

const StyledFormikInput = styled(FormikInput)`
  .MuiInputBase-root {
    height: fit-content;
  }
`;

const NameFieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 100px;
  align-items: start;
  gap: 15px;
`;

const DurationContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 10px;
`;

const EstimatedDurationText = styled(Typography)`
  color: ${colors.shades.secondary3};
  font-size: 14px;
  white-space: nowrap;
`;

const DurationInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DurationInput = styled.input`
  width: 60px;
  height: 35px;
  padding: 4px 8px;
  font-size: 14px;
  border: 1px solid ${colors.shades.secondary7};
  border-radius: 4px;
  color: ${colors.base.secondary2};
  outline: none;

  &:focus {
    border-color: ${colors.base.primary};
  }
`;

const DurationMinLabel = styled.span`
  font-size: 13px;
  color: ${colors.shades.secondary3};
`;

const DurationAutoHint = styled.span`
  font-size: 11px;
  color: ${colors.shades.secondary3};
  cursor: pointer;
  margin-top: 4px;
  text-decoration: underline;
  white-space: nowrap;

  &:hover {
    color: ${colors.base.primary};
  }
`;

const SessionTypeContainer = styled.div`
  margin-top: 10px;
  /* The side gutter comes from StyledRadioButtonGroup below, which carries its own 45px margin. */
  margin-left: 45px;
`;

const StyledRadioButtonGroup = styled(RadioButtonGroup)`
  margin: 10px 45px;
  display: flex;
  width: auto;
  .MuiFormControlLabel-root {
    margin-top: 10px;
  }
`;

const WorkoutSubDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 10px;
`;

const WorkoutIdValue = styled.div`
  padding: 5px 0;
  font-size: 16px;
  color: ${colors.base.secondary2};
  line-height: 148%;
`;

const ClipboardName = styled(Typography)`
  font-size: 14px;
  color: ${colors.base.secondary2};
  line-height: 148%;
`;

const ClipboardExerciseItem = styled(Typography)`
  font-size: 12px;
  color: ${colors.shades.secondary3};
  line-height: 148%;
  padding-left: 4px;

  &::before {
    content: '· ';
  }
`;

export {
  FLASH_SECONDS,
  FLASH_CLASSNAME,
  Container,
  StyledForm,
  StyledSectionCompartment,
  ContentContainer,
  InfoChip,
  ScrollableContainer,
  StyledFormikInput,
  NameFieldRow,
  DurationContainer,
  EstimatedDurationText,
  DurationInputRow,
  DurationInput,
  DurationMinLabel,
  DurationAutoHint,
  StyledRadioButtonGroup,
  SessionTypeContainer,
  WorkoutSubDetailsContainer,
  WorkoutIdValue,
  ClipboardName,
  ClipboardExerciseItem,
};
